import { LucideIcon } from 'lucide-react';
import {
  Actions,
  IntervalE,
  Subjects,
  ShipmentTransportModes,
  ShipmentTransportStatusTypes,
  ShipmentTransportHealthDetails,
  PartyTypes
} from './enums';
import { MongoAbility } from '@casl/ability';
import type { Position } from '@deck.gl/core';
import { ShipmentView } from '@prisma/client';

export type GenericResponse = {
  success: boolean;
  message: string;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  data?: any;
};

export type NavItem = {
  title: string;
  link: string;
};

export type NavItemSidebar = {
  title: string;
  link: string;
  icon: LucideIcon;
};

export interface LayoutProps {
  children: React.ReactNode;
  params?: { vulcanId: string };
}

export interface PlanI {
  name: string;
  interval?: IntervalE;
  price?: string;
  price_id?: string;
  isPopular?: boolean;
}

export interface ProductI {
  name: string;
  description: string;
  features: string[];
  plans: PlanI[];
}

export interface UserRoleWithLogo {
  userRoleId: string;
  vulcanId: string;
  roleCode: string;
  customerName: string;
  logo?: string; // Virtual field to hold the organization's logo
  isActive?: boolean;
}

export type AppAbility = MongoAbility<[Actions, Subjects]>;

export const emailNotificationCategories = [
  {
    id: 'account',
    title: 'Account & Security',
    description: 'Password changes, login alerts, and security updates',
    subcategories: [
      { id: 'security_alerts', label: 'Security Alerts', default: true },
      { id: 'account_changes', label: 'Account Changes', default: true },
      { id: 'login_attempts', label: 'New Login Attempts', default: true }
    ]
  },
  {
    id: 'updates',
    title: 'Updates & News',
    description: 'Platform updates, new features, and announcements',
    subcategories: [
      { id: 'product_updates', label: 'Product Updates', default: false },
      { id: 'new_features', label: 'New Features', default: true },
      { id: 'newsletters', label: 'Newsletters', default: false }
    ]
  },
  {
    id: 'reminders',
    title: 'Reminders & Deadlines',
    description: 'Important deadlines and scheduled reminders',
    subcategories: [
      { id: 'upcoming_events', label: 'Upcoming Events', default: true },
      { id: 'task_deadlines', label: 'Task Deadlines', default: true }
    ]
  }
];

// Type for the subscription information
export type SubscriptionInfo = {
  plan: string; // Name of the subscription plan
  status: string; // Status of the subscription
  renewalDate: string; // Renewal date in ISO format
  featuresUsed: {
    projects: string; // Usage of projects in "used/total" format
    members: string; // Usage of members in "used/total" format
    participants: string; // Usage of participants in "used/total" format
    scenarios: string; // Usage of scenarios in "used/total" format
  };
};

export type RecentActivityItem = {
  name: string;
  type: string;
  date: string;
};

export type AppNote = {
  id: string;
  content: string;
  createdAt: string;
  createdBy: string;
  modifiedAt: string;
  modifiedBy: string;
};

// Track & Trace Dashboard types
export type ShipmentStatusMetricsByPeriod = {
  shipment_period?: string;
  shipment_mode: string;
  active_shipments: number;
  active_shipments_change: string;
  shipment_volume: string;
  shipment_volume_change: string;
  shipment_risks: ShipmentRisk;
  shipment_count: number;
};

export type ShipmentMode = 'ROAD' | 'OCEAN' | 'AIR' | 'RAIL';
export type TimePeriod =
  | 'Today'
  | 'Yesterday'
  | 'This Week'
  | 'Last Week'
  | 'This Month'
  | 'Last Month'
  | 'This Quarter'
  | 'Last Quarter'
  | 'This Year'
  | 'Last Year';

export interface ShipmentRisk {
  count: number;
  type: string;
}

export interface ShipmentRisks {
  high: ShipmentRisk;
  medium: ShipmentRisk;
  low: ShipmentRisk;
}

export interface ShipmentMetrics {
  period: TimePeriod;
  mode: ShipmentMode;
  activeShipments: number;
  activeShipmentsChange: string;
  volume: string;
  volumeChange: string;
  risks: ShipmentRisks;
  total: number;
}

export interface ShipmentMetricsData {
  total: number;
  onTime: number;
  late: number;
  early: number;
  inTransit: number;
}

export interface ActiveOnTimeShipmentMetrics {
  shipmentPeriod: TimePeriod;
  shipmentResult: ShipmentMetricsResult;
}

export interface ShipmentMetricsResult {
  trend: string;
  active_shipments: {
    fill: string;
    mode: string;
    shipments: string;
  }[];
}

export interface MonthlyShipmentExceptionMetrics {
  shipmentExceptions: {
    trend: ModalityTrendData;
    historical_data: MonthlyShipmentException[];
  };
}

// For trend data
export interface ModalityTrendData {
  [mode: string]: string; // This allows for dynamic percentage strings per mode
}

export interface MonthlyShipmentException {
  index: number;
  month: string;
  [mode: string]: number | string;
}

export interface MonthlyShipmentExceptionMetrics {
  shipment_exceptions: {
    trend: ModalityTrendData;
    historical_data: MonthlyShipmentException[];
  };
}

export interface ShipmentVolumeCountMetrics {
  shipment_results: {
    trend: {
      count: string;
      volume: string;
    };
    historical_data: ShipmentVolumeCount[];
  };
}

export interface ShipmentVolumeCount {
  count: number;
  index: number;
  month: string;
  volume: number;
}

export interface ShipmentDeliveryForecastMetrics {
  vulcanId: string;
  daySequence: number;
  deliveryDate: Date;
  day: string;
  date: string;
  modes: {
    [key: string]: {
      count: number;
      percentage: number;
    };
  };
  total: number;
}

export type GetShipmentDeliveryForecastParams = {
  vulcanId?: string;
  startDate?: Date;
  numDays?: number;
  modes?: string[];
  customers?: IdValue[];
};

export type IdValue = {
  id: string;
  value: string;
  description?: string;
};

export type ZeusUserLight = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  status: number;
  avatar: string;
  language: string;
  timezone: string;
  welcome_survey_completed: boolean;
};

export type VulcanContactInfo = { 
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  is_active: boolean;
  contact_role: string;
};

// Map Interfaces
export interface ShipmentData {
  id: string;
  origin: number[] | Position;
  destination: number[] | Position;
  mode: string | ShipmentMode;
  originCode?: string;
  destinationCode?: string;
}

export interface RouteSegment {
  sourcePosition: number[] | Position;
  targetPosition: number[] | Position;
  mode: ShipmentMode;
  id: string;
  path?: number[] | Position[];
}

export interface ShipmentMapProps {
  shipments?: ShipmentData[];
  showBorder?: boolean;
  onTilesLoad?: () => void;
  onShipmentClick?: (id: string) => void;
  selectedShipmentId?: string | null;
  lineWidth?: number;
}

//export type MapHoverInfo = ShipmentData | null;
export interface MapHoverInfo {
  id: string;
  mode: string;
  origin: [number, number];
  destination: [number, number];
  originCode?: string;
  destinationCode?: string;
  // New properties for pin hover
  x?: number;
  y?: number;
  pointType?: 'Origin' | 'Destination';
  code?: string;
}

export interface ShipmentGroupedMetrics {
  vulcan_id: string;
  shipment_mode: string;
  shipment_status: string;
  shipment_health_detail: string;
  count: number;
}

export interface ShipmentMapData extends ShipmentView {
  origin: Position;
  destination: Position;
}

export interface ShipmentMapCluster {
  coordinates: Position;
  count: number;
  byHealth: Map<string, number>;
  type: 'origin' | 'destination';
}

export interface ShipmentMapHoverInfo {
  x: number;
  y: number;
  content: string;
}

export type ShipmentMapView = {
  shipment_pk_id: number;
  vulcan_id: string;
  vendor_shipment_id: string;
  shipment_mode: string;
  shipment_status: string;
  shipment_health_detail: string;
  origin_location_latitude: string;
  origin_location_longitude: string;
  origin_location_name: string;
  origin_location_city_name: string;
  origin_location_country: string;
  destination_location_latitude: string;
  destination_location_longitude: string;
  destination_location_name: string;
  destination_location_city_name: string;
  destination_location_country: string;
  current_milestone_description: string;
  vulcan_estimated_delivery_date_time: Date;
  completion_percentage: number;
  is_delivered: boolean;
  is_overdue: boolean;
};

// Filter Interfaces
export interface ShipmentFilterValues {
  shipmentDateRange?: {
    start: Date | null;
    end: Date | null;
  };
  deliveryDateRange?: {
    start: Date | null;
    end: Date | null;
  };
  defaultDateRange?: {
    shipment?: { start: Date; end: Date };
    delivery?: { start: Date; end: Date };
  };
  dateConditionType?: 'AND' | 'OR';
  modes?: ShipmentTransportModes[];
  statuses?: ShipmentTransportStatusTypes[];
  healthDetails?: ShipmentTransportHealthDetails[];
  shipmentNumber?: string;
  bookingNumber?: string;
  mAWB?: string;
  containerNumber?: string;
}

export interface CarrierFilterValues {
  carrier_code?: string;
  carrier_name?: string;
}

export interface PartyDetails {
  location_id?: number;
  party_id?: number;
  party_code?: string;
  party_name?: string;
  party_types?: PartyTypes[];
  default_location_name?: string;
  default_street_address?: string;
  default_street_address2?: string;
  default_street_address3?: string;
  default_street_address4?: string;
  default_city_name?: string;
  default_state_province?: string;
  default_zip_postal?: string;
  default_country?: string;
  default_country_code?: string;
  default_latitude?: string;
  default_longitude?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  customer_name?: string;
  customer_code?: string;
}

export interface ZipCodeDetails {
  id?: string | number;
  zipcode_id?: string | number;
  address_id?: string | number;
  display_name?: string;
  display_name_short?: string;
  place_name?: string;
  city_name?: string;
  state_province?: string;
  admin_name1?: string;
  postal_code?: string;
  zip_postal?: string;
  latitude?: number;
  longitude?: number;
}

// Page Properties
export interface DefaultPageConfig {
  showAllFields?: boolean;
  showMap?: boolean;
}

export interface ShipmentPageConfig {
  excludeStatuses?: ShipmentTransportStatusTypes[];
  showAllStatuses?: boolean;  // true for all shipments, false for track & trace
  defaultDateRange?: {
    shipment?: { start: Date; end: Date };
    delivery?: { start: Date; end: Date };
  };
  dateConditionType?: 'AND' | 'OR';
  showMap?: boolean;
}

export const paths = {
  dashboard: {
    dashboardBase: '/dashboard/',
    toDashboard: '/',
    settings: {
      toPaymentResult: '/billing_subscription/payment_result'
    },
    todos: { toMyTodos: '/todos/my-todos', createTodos: '/todos/create' }
  },
  auth: {
    toLogin: '/auth/login',
    requireAuth: '/auth/login',
    authConfirm: '/auth/confirmed',
    callback: '/api/auth-callback'
  },
  user: {
    toUserDashboard: '/user/dashboard',
    toOrgInvite: '/user/org-invite'
  }
} as const;

// Type for the paths object
export type Paths = typeof paths;

// Utility type to get specific path sections
export type AuthPaths = typeof paths.auth;
export type DashboardPaths = typeof paths.dashboard;
export type UserPaths = typeof paths.user;

export interface ShipmentSearchResult {
  id: string;
  type: 'awb' | 'mawb' | 'booking' | 'company name' | 'shipment';
  title: string;
  content: string;
  rank: number;
  allMatches: {
    awb?: string;
    mawb?: string;
    booking?: string;
    shipper?: string;
    consignee?: string;
    customer?: string;
    shipper_code?: string;
    consignee_code?: string;
    customer_code?: string;
    shipment_mode?: string;
    shipment_status?: string;
  };
  shipmentDate?: Date;
  deliveryDate?: Date;
  shipmentMode?: string;
  shipmentStatus?: string;
  origin?: string;
  destination?: string;
}

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export interface TopHTSCodesView {
  hts_number: string;
  full_description: string;
  current_general_rate: string;
  prev_general_rate: string;
  change_pct: string;
}

export interface RevisionsView {
  revision_id: string;
  revision_number: string;
  effective_date: Date
  revision_desc: string;
}

export interface HTSEntriesView {
  revision_id: string;
  entry_id: string;
  hts_number: string;
}

// Tariff Data
// Complete TariffMaster Type Definition
export interface TariffMasterData {
  id: string;
  revision_id: string;
  is_active: boolean;
  status?: number;

  // Basic Information
  hts_number: string;
  formatted_hts_number: string;
  brief_description: string;

  // Quantity Codes
  quantity_1_code?: string;
  quantity_2_code?: string;

  // WTO Information
  wto_binding_code?: string;

  // MFN Rate Information
  mfn_text_rate?: string;
  mfn_rate_type_code?: string;
  mfn_ave?: string;
  mfn_ad_val_rate?: string;
  mfn_specific_rate?: string;
  mfn_other_rate?: string;

  // Special Program Information
  col1_special_text?: string;
  col1_special_mod?: string;

  // GSP Information
  gsp_indicator?: string;
  gsp_ctry_excluded?: string;

  // Special Program Indicators
  apta_indicator?: string;
  civil_air_indicator?: string;
  pharmaceutical_ind?: string;
  dyes_indicator?: string;
  agoa_indicator?: string;
  israel_fta_indicator?: string;
  atpdea_indicator?: string;
  nepal_indicator?: string;

  // CBI Information
  cbi_indicator?: string;
  cbi_ad_val_rate?: string;
  cbi_specific_rate?: string;

  // CBTPA Information
  cbtpa_indicator?: string;
  cbtpa_rate_type_code?: string;
  cbpta_ad_val_rate?: string;
  cbtpa_specific_rate?: string;

  // ATPA Information
  atpa_indicator?: string;
  atpa_ad_val_rate?: string;
  atpa_specific_rate?: string;

  // FTA Rate Information - Multiple Countries
  // Each FTA has indicator, rate_type_code, ad_val_rate, specific_rate, and other_rate

  // Jordan
  jordan_indicator?: string;
  jordan_rate_type_code?: string;
  jordan_ad_val_rate?: string;
  jordan_specific_rate?: string;
  jordan_other_rate?: string;

  // Singapore
  singapore_indicator?: string;
  singapore_rate_type_code?: string;
  singapore_ad_val_rate?: string;
  singapore_specific_rate?: string;
  singapore_other_rate?: string;

  // Korea
  korea_indicator?: string;
  korea_rate_type_code?: string;
  korea_ad_val_rate?: string;
  korea_specific_rate?: string;
  korea_other_rate?: string;

  // Japan
  japan_indicator?: string;
  japan_rate_type_code?: string;
  japan_ad_val_rate?: string;
  japan_specific_rate?: string;
  japan_other_rate?: string;

  // Chile
  chile_indicator?: string;
  chile_rate_type_code?: string;
  chile_ad_val_rate?: string;
  chile_specific_rate?: string;
  chile_other_rate?: string;

  // Peru
  peru_indicator?: string;
  peru_rate_type_code?: string;
  peru_ad_val_rate?: string;
  peru_specific_rate?: string;
  peru_other_rate?: string;

  // Colombia
  columbia_indicator?: string;
  columbia_rate_type_code?: string;
  columbia_ad_val_rate?: string;
  columbia_specific_rate?: string;
  columbia_other_rate?: string;

  // Panama
  panama_indicator?: string;
  panama_rate_type_code?: string;
  panama_ad_val_rate?: string;
  panama_specific_rate?: string;
  panama_other_rate?: string;

  // Bahrain
  bahrain_indicator?: string;
  bahrain_rate_type_code?: string;
  bahrain_ad_val_rate?: string;
  bahrain_specific_rate?: string;
  bahrain_other_rate?: string;

  // Oman
  oman_indicator?: string;
  oman_rate_type_code?: string;
  oman_ad_val_rate?: string;
  oman_specific_rate?: string;
  oman_other_rate?: string;

  // Morocco
  morocco_indicator?: string;
  morocco_rate_type_code?: string;
  morocco_ad_val_rate?: string;
  morocco_specific_rate?: string;
  morocco_other_rate?: string;

  // Australia
  australia_indicator?: string;
  australia_rate_type_code?: string;
  australia_ad_val_rate?: string;
  australia_specific_rate?: string;
  australia_other_rate?: string;

  // USMCA
  usmca_indicator?: string;
  usmca_rate_type_code?: string;
  usmca_ad_val_rate?: string;
  usmca_specific_rate?: string;
  usmca_other_rate?: string;

  // NAFTA
  nafta_canada_ind?: string;
  nafta_mexico_ind?: string;
  mexico_rate_type_code?: string;
  mexico_ad_val_rate?: string;
  mexico_specific_rate?: string;

  // DR-CAFTA
  dr_cafta_indicator?: string;
  dr_cafta_rate_type_code?: string;
  dr_cafta_ad_val_rate?: string;
  dr_cafta_specific_rate?: string;
  dr_cafta_other_rate?: string;

  // DR-CAFTA Plus
  dr_cafta_plus_indicator?: string;
  dr_cafta_plus_rate_type_code?: string;
  dr_cafta_plus_ad_val_rate?: string;
  dr_cafta_plus_specific_rate?: string;
  dr_cafta_plus_other_rate?: string;

  // Column 2 Information
  col2_text_rate?: string;
  col2_rate_type_code?: string;
  col2_ad_val_rate?: string;
  col2_specific_rate?: string;
  col2_other_rate?: string;

  // Additional Information
  begin_effect_date?: string;
  end_effective_date?: string;
  additional_duty?: string;
  footnote_comment?: string;
}

// Duty Calculation Interfaces
export interface DutyInput {
  value: number;
  freight: number;
  insurance: number;
  otherCosts: number;
}

export interface DutyCalculation {
  dutiableValue: number;
  dutyAmount: number;
  totalLandedCost: number;
  requiresSpecialCalculation?: boolean;
}

// Program Rate Result Interface
export interface ProgramRateResult {
  programName: string;
  calculation: DutyCalculation;
  isPreferential: boolean;
}

// Quantity Input Interface
export interface QuantityInput {
  q1?: number;
  q2?: number;
}

export interface ShippingItemData {
  pieceCount: number | null;
  palletCount: number | null;
  length: number | null;
  width: number | null;
  height: number | null;
  weight: number | null;
  weightType: number | null;
  productClass: number | null;
  linearFeet: number | null;
  nmfcNumber: string | null;
  description: string | null;
  packageType: number | null;
  packageTypeName?: string;
  hazmat: boolean;
  hazmatClass: number | null;
  packingGroupNumber: string | null;
  unPoNumber: string | null;
  stackable: boolean;
  density: number | null;
  dimUnit: string | null; // 'in' or 'cm'
  weightUnit: string | null; // 'lbs' or 'kg'
}

export interface QuoteRequest {
  CustomerId: string;
  GuaranteedRates: boolean;
  PickupDate: string;
  DeliveryDate?: string;
  ExtremeLength?: number;
  ExtremeLengthBundleCount?: number;
  Stackable: boolean;
  TerminalPickup: boolean;
  ContactName: string;
  ValueOfGoods: number;
  ShipmentNew: boolean;
  //ServiceType: string;
  Origin: {
    Street: string;
    City: string;
    State: string;
    Zip: string;
    Country: string;
  };
  Destination: {
    Street: string;
    City: string;
    State: string;
    Zip: string;
    Country: string;
  };
  Items: Array<{
    PieceCount: number;
    PalletCount: number;
    Length: number;
    Width: number;
    Height: number;
    Weight: number;
    WeightType: number;
    ProductClass: number;
    LinearFeet: number;
    NmfcNumber: string;
    Description: string;
    PackageType: number;
    Hazmat: boolean;
    HazmatClass?: number;
    PackingGroupNumber: string;
    UnPoNumber: string;
    Stackable: boolean;
  }>;
  Accessorials: number[];
}

export interface CreateQuoteRequest {
  vulcanId: string;
  quoteType: string;
  quoteRequest: QuoteRequest;
  quoteProviderName: string;
  quoteFormData: QuoteFormData;
  quoteName?: string; // Optional name for the quote
}

export interface UpsertQuoteRequest extends CreateQuoteRequest {
  quoteId?: number; // Optional - if provided, it's an update operation
}

export interface QuoteFormData {
  quoteId?: number;
  formData: {
    //customerId: string;
    vulcanId: string;
    quoteName?: string;
    guaranteedRates: boolean;
    pickupDate: Date;
    extremeLength?: number;
    extremeLengthBundleCount?: number;
    stackable: boolean;
    terminalPickup: boolean;
    contactName: string;
    valueOfGoods: number;
    shipmentNew: boolean;
    quote_provider: string;
    quote_provider_id: number; 
    service_type: string;
    currency: string;
    specialServices: number[];
    equipment_type?: string;
    equipment_length?: string;
  };
  origin: {
    street: string;
    city: string;
    state: string;
    zip: string;
    partyId: string;   
    country: string;
    companyName: string;
    contactName: string;
    contactPhone: string;   
    cityStateZip: string;
    latitude: number;
    longitude: number;
    openTime: string;
    closeTime: string;
    remarks: string;
    siteType: string;
    pickupDate: Date;
    accessorials: number[];
  };
  destination: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    partyId: string;    
    companyName: string;
    contactName: string;
    contactPhone: string;  
    cityStateZip: string;
    latitude: number;
    longitude: number;
    openTime: string;
    closeTime: string;
    remarks: string;
    siteType: string;
    accessorials: number[];
    deliveryDate: Date;
  };
  items: Array<{
    pieceCount: number;
    palletCount: number;
    length: number;
    width: number;
    height: number;
    weight: number;
    weightType: number;
    productClass: number;
    linearFeet: number;
    nmfcNumber: string;
    description: string;
    packageType: number;
    hazmat: boolean;
    hazmatClass?: number;
    packingGroupNumber: string;
    unPoNumber: string;
    stackable: boolean;
    density?: number;
  }>;
  specialServices: number[];  
  references: QuoteReference;
}

export interface QuoteReference {
  customerBOLNo: string;
  shipperRefNo: string;
  pickupNumber: string;
  poNo: string;
  labelNotes: string;
}

export interface QuoteValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export interface QuoteProvider {
  name: string;
  authenticate(): Promise<string | null>;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  submitQuoteRequest(request: QuoteRequest, authToken?: string): Promise<any>;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  transformRequest?(request: QuoteRequest): any;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  transformResponse?(response: any): any;
}

// Provider interface for shipment creation
export interface ShipmentProvider {
  createSupplySense360ShipmentRecord(vulcanId: string, quoteId: number, providerResponse: any, originalQuoteFormData: QuoteFormData, createResult: CreateShipmentResponse): Promise<SupplySense360ShipmentRecord | null>;
  name: string;
  authenticate(): Promise<string | null>;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  submitShipmentRequest(request: any, authToken?: string): Promise<any>;
}


export interface GenericShipmentRequest {
  provider: string;
  mode: string;
  customerId: string;
  externalQuoteId: string;
  carrierId: string;
  carrierName: string;
  customerBOLNo?: string;
  shipperRefNo?: string;
  pickupNumber?: string;
  poNo?: string;
  labelNotes?: string;
  pickupRemarks?: string;
  deliveryRemarks?: string;
  origin: {
    companyName?: string;
    street: string;
    street2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    latitude?: string;
    longitude?: string;
    contactName?: string;
    phoneNumber?: string;
    readyTime: string;
    closeTime: string;
  };
  destination: {
    companyName?: string;
    street: string;
    street2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    latitude?: string;
    longitude?: string;
    contactName?: string;
    phoneNumber?: string;
    readyTime: string;
    closeTime: string;
  };
  /* eslint-disable @typescript-eslint/no-explicit-any */
  providerSpecificData?: any;
}

export interface CreateShipmentRequest {
  vulcanId: string;
  quoteProvider: string; // Name of the shipment quote provider
  quoteId: number; // Internal quote ID
  responseId: number; // Quote response ID that's being booked
  carrierId: string; // Carrier ID from the selected quote
  carrierCode: string; // Carrier code from the selected quote
  carrierName: string; // Carrier name from the selected quote
  shipmentRequest: GenericShipmentRequest;
}

/*
// DUPLICATE?
export interface CreateShipmentResponse extends GenericResponse {
  shipmentId?: number;
  shipmentNumber?: string;
  shipperPartyId?: number;
  consigneePartyId?: number;
  carrierCode?: string;
  originLocationId?: number;
  destinationLocationId?: number;
}*/

export enum QuoteStatus {
  DRAFT = 'DRAFT',
  RATES_REQUESTED = 'RATES_REQUESTED',
  RATES_RECEIVED = 'RATES_RECEIVED',
  // PENDING = 'PENDING',
  // RECEIVED = 'RECEIVED',
  // REVIEWED = 'REVIEWED',
  // ACCEPTED = 'ACCEPTED',
  // REJECTED = 'REJECTED',
  BOOKED = 'BOOKED',
  // CONFIRMED = 'CONFIRMED',
  // IN_TRANSIT = 'IN_TRANSIT',
  // DELIVERED = 'DELIVERED',
  // CANCELLED = 'CANCELLED',
  // EXPIRED = 'EXPIRED',
  ERROR = 'ERROR'
}

export enum QuoteShipmentStatus {
  DRAFT = 'DRAFT',
  PENDING = 'PENDING',
  BOOKED = 'BOOKED',
  CONFIRMED = 'CONFIRMED',
  SHIPPED = 'SHIPPED',
  DELIVERED = 'DELIVERED',
  CANCELLED = 'CANCELLED',
  ERROR = 'ERROR'
}

export interface QuoteFilterValues {
  quote_status?: string;
  quote_type?: string;
  quote_number?: string;
  quote_name?: string;
  origin?: string;
  destination?: string;
  selected_carrier?: string;
  customer_name?: string;
  shipping_date_from?: string;
  shipping_date_to?: string;
}

export interface ContractedRatesFilterValues {
  pickup_region?: string;
  delivery_region?: string;
  carrier?: string;
  equipment?: string;
}

export interface TrackShipmentRequest {
  quoteId: number;
  quoteShipmentId: number;
  bolNumber: string;
  destinationZip: string;
  vulcanId: string;
  shipmentPkId: number;
  shipperPartyId: number;
  consigneePartyId: number;
}

export interface TrackShipmentResponse {
  success: boolean;
  message: string;
  trackingId?: number;
  error?: string;
}

export interface ShipmentTrackingProvider {
  name: string;
  authenticate(): Promise<string | null>;
  trackShipment(bolNumber: string, destinationZip: string): Promise<any>;
}

export interface TrackingEvent {
  timestamp: string;
  location: string;
  status: string;
  description: string;
}

export interface ShipmentTrackingResponse {
  success: boolean;
  message: string;
  trackingId?: number;
  trackingData?: {
    bolNumber: string;
    carrierName: string;
    status: string;
    estimatedDelivery?: string;
    currentLocation?: string;
    events: TrackingEvent[];
  };
  error?: string;
}

// Update your CreateShipmentResponse to include tracking-related fields
export interface CreateShipmentResponse extends GenericResponse {
  shipmentId?: number;
  shipmentNumber?: string;
  shipperPartyId?: number;
  consigneePartyId?: number;
  carrierCode?: string;
  originLocationId?: number;
  destinationLocationId?: number;
  bolNumber?: string; // Add this for tracking
  shipmentPkId?: number; // Add this for tracking
}

export interface SupplySense360ShipmentRecord {
  success: boolean;
  shipmentId?: number;
  bolNumber?: string;
  shipmentPkId?: number;
  destinationZip?: string;
  trackingReady?: boolean;
  [key: string]: any;
}

export interface TerminalAddress {
  companyName: string;
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  phone?: string;
  contactName?: string;
  email?: string;
}

// Main Shipment Data Type
export interface ProviderShipmentData {
  vulcanId: string;
  tenantId: number;
  vendorShipmentId?: string;
  shipmentMode?: string;
  businessType?: string;
  shipmentStatus: string;
  shipperPartyId: number;
  consigneePartyId: number;
  customerPartyId?: number;
  originLocationId: number;
  destinationLocationId: number;
  currentMilestoneDateTime: Date;
  requestedShipDateTime?: Date;
  requestedDeliveryDateTime?: Date;
  estimatedDeliveryDateTime?: Date;
  bookingNumber?: string;
  shipperPo?: string;
  consigneePo?: string;
  carrierPo?: string;
  freightClass?: string;
  grossWeight?: number;
  grossWeightUnit?: string;
  chargedWeight?: number;
  chargedWeightUnit?: string;
  totalPieces?: number;
  invoiceAmount?: number;
  invoiceCurrency?: string;
  carrierCode?: string;
  totalVolume?: number;
  totalVolumeUom?: string;
  totalPiecesUom?: string;
  serviceType: string;
  includesHazmat?: boolean;
  bookingDateTime?: Date;
  originTerminalAddress?: TerminalAddress;
  destinationTerminalAddress?: TerminalAddress;
  references: QuoteReference;
  pickup_remarks?: string;
  delivery_remarks?: string;
  provider_id?: number; 
}

export interface ShipmentReferencesProps {
  formData: {
    customerBOLNo?: string;
    shipperRefNo?: string;
    pickupNumber?: string;
    poNo?: string;
    labelNotes?: string;
  };
  isViewMode?: boolean;
  isSubmitting?: boolean;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  setFormData: (data: any) => void;
}

export interface ShipmentReferencesFormType {
  customerBOLNo: string;
  shipperRefNo: string;
  pickupNumber: string;
  poNo: string;
  labelNotes: string;
}

export interface ProductBookItem {
  id?: number;
  productDescription: string;
  pieceCount?: number;
  palletCount?: number;
  length?: number;
  width?: number;
  height?: number;
  dimensionUnit?: string;
  weight?: number;
  weightUnit?: string;
  freightClass?: number;
  linearFeet?: number;
  packageType?: number;
  packageTypeDescription?: string;
  nmfcNumber?: string;
  isHazmat?: boolean;
  hazmatClass?: number;
  packingGroupNumber?: string;
  unPoNumber?: string;
  isStackable?: boolean;
  density?: number;
  vulcanId: string;
}

// Interface for provider details response
export interface ProviderMasterDetail {
  id: bigint;
  vulcanId: string;
  identifier: string; // maps to 'identifier' column, not 'key'
  value: any; // jsonb can contain any valid JSON value
  isActive: boolean;
  isDeleted: boolean;
  deletedAt: Date | null;
  deletedBy: string | null; 
  createdAt: Date;
  createdBy: string | null; 
  modifiedAt: Date;
  modifiedBy: string | null; 
  version: number;
}

// Analytics Interfaces
// Road Analytics Filter State
export interface RoadFilterState {
  dateRange: string;
  customerId: string;
  operatorId: string;
  serviceType: string;
  fromDate: string;
  toDate: string;
}

// Road Analytics Data Response
export interface RoadAnalyticsData {
  totalSavings: number;
  savingsChange: string;
  totalShipments: number;
  shipmentsChange: string;
  quotesGenerated: number;
  quotesChange: string;
  conversionRate: number;
  conversionChange: string;
  avgSavings: number;
  avgSavingsChange: string;
  onTimeRate: number;
  onTimeChange: string;
}

// Road Metric Card Configuration
export interface RoadMetric {
  id: string;
  title: string;
  value: string | number;
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral';
  icon: React.ComponentType<{ className?: string }>;
  format?: 'currency' | 'percentage' | 'number' | 'text';
}

// Analytics Chart Data Interfaces
export interface LaneSavingsData {
  lane: string;
  originCity: string;
  destinationCity: string;
  savings: number;
  shipmentCount: number;
  avgSavingsPerShipment: number;
}

export interface TrendData {
  period: string;
  date: string;
  shipments: number;
  quotes: number;
  conversionRate: number;
  avgSavings: number;
}

export interface LaneMatrixData {
  fromCity: string;
  toCity: string;
  shipmentCount: number;
  totalSavings?: number;
  avgSavingsPerShipment?: number;
}

// Combined chart data response
export interface RoadAnalyticsChartData {
  laneSavings: LaneSavingsData[];
  shipmentTrends: TrendData[];
  laneMatrix: LaneMatrixData[];
}

// Carrier Performance Data
export interface CarrierPerformanceData {
  carrierCode: string;
  carrierName: string;
  shipmentCount: number;
  onTimePercentage: number;
  avgTransitTime: number;
  damageRate: number;
  avgCostPerPound: number;
  totalCost: number;
  overallScore: 'Excellent' | 'Good' | 'Fair' | 'Poor';
  scoreValue: number; // For sorting
}

// Operator Performance Data

export interface OperatorPerformanceData {
  operatorId: string;
  operatorName: string;
  quotesCreated: number;
  shipmentsBooked: number;
  conversionRate: number;
  avgSavingsGenerated: number;
  totalSavings: number;
  avgQuoteResponseTime: number; // in hours
  customerSatisfactionScore?: number; // if available
}

export interface ProcessQuoteResult {
  success: boolean;
  message: string;
  quoteId?: number;
  quoteNumber?: string;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  responses?: any[];
  errors?: string[];
}

export type ShipmentListReferences = {
  PO_NUMBER?: string;
  SHIPPER_REF?: string;
  CUSTOMER_BOL?: string;
  PICKUP_NUMBER?: string;
};

export interface InvoiceAuditFilterValues {
  carrier?: string;
  status?: 'passed' | 'warning' | 'failed';
  dateFrom?: string;
  dateTo?: string;
}